import { useEffect, useRef, useState } from "react";
import { gisMarkers, type GisMarker } from "@/mocks/gis";
import MarkerPopup from "./MarkerPopup";

declare global {
  interface Window {
    L: typeof import("leaflet");
  }
}

interface Props {
  filterTypes: string[];
  searchText: string;
}

const markerColors: Record<string, string> = {
  resident: "#22c55e",
  commercial: "#E8531A",
  alarm: "#ef4444",
  station: "#3b82f6",
  pipeline: "#8b5cf6",
};

const markerIcons: Record<string, string> = {
  resident: "🏠",
  commercial: "🏢",
  alarm: "⚠️",
  station: "🔧",
  pipeline: "📍",
};

// 3D柱状高度（根据用气量/重要性）
const getHeight = (marker: GisMarker): number => {
  if (marker.type === "alarm") return 80;
  if (marker.type === "station") return 60;
  if (marker.type === "commercial") return Math.min(100, 20 + (marker.monthlyUsage || 0) / 500);
  if (marker.type === "resident") return 20 + (marker.monthlyUsage || 0) * 0.5;
  return 30;
};

export default function GisMap3D({ filterTypes, searchText }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const markersRef = useRef<unknown[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<GisMarker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const filteredMarkers = gisMarkers.filter(m => {
    const typeMatch = filterTypes.length === 0 || filterTypes.includes(m.type);
    const searchMatch = !searchText || m.name.includes(searchText) || m.address.includes(searchText);
    return typeMatch && searchMatch;
  });

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const loadLeaflet = async () => {
      if (!window.L) {
        await new Promise<void>((resolve) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
          document.head.appendChild(link);

          const script = document.createElement("script");
          script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
          script.onload = () => resolve();
          document.head.appendChild(script);
        });
      }

      const L = window.L;
      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        center: [39.9042, 116.4074],
        zoom: 11,
        zoomControl: false,
      });

      // 使用深色地图瓦片，更有3D科技感
      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 19,
      }).addTo(map);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      mapInstanceRef.current = map;
      setMapLoaded(true);
    };

    loadLeaflet();

    return () => {
      if (mapInstanceRef.current) {
        (mapInstanceRef.current as { remove: () => void }).remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) return;
    const L = window.L;
    const map = mapInstanceRef.current as { addLayer: (l: unknown) => void; removeLayer: (l: unknown) => void };

    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    filteredMarkers.forEach(marker => {
      const color = markerColors[marker.type] || "#666";
      const height = getHeight(marker);
      const isAlarm = marker.type === "alarm" || marker.alarmLevel === "紧急";

      // 3D柱状图标
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
        ">
          <!-- 3D柱体 -->
          <div style="
            width: 16px;
            height: ${height}px;
            background: linear-gradient(to right, ${color}cc, ${color}, ${color}99);
            border-radius: 3px 3px 0 0;
            position: relative;
            box-shadow: 3px 3px 0 ${color}55;
            ${isAlarm ? "animation: glow 1.5s infinite;" : ""}
          ">
            <!-- 顶面 -->
            <div style="
              position: absolute;
              top: -6px;
              left: -4px;
              width: 24px;
              height: 12px;
              background: ${color};
              border-radius: 50%;
              transform: perspective(20px) rotateX(30deg);
              opacity: 0.9;
            "></div>
          </div>
          <!-- 底部标签 -->
          <div style="
            background: ${color};
            color: white;
            font-size: 10px;
            padding: 1px 4px;
            border-radius: 3px;
            white-space: nowrap;
            margin-top: 2px;
            max-width: 60px;
            overflow: hidden;
            text-overflow: ellipsis;
          ">${markerIcons[marker.type]} ${marker.name.length > 4 ? marker.name.slice(0, 4) + ".." : marker.name}</div>
        </div>
        ${isAlarm ? `<style>@keyframes glow { 0%,100%{box-shadow:3px 3px 0 ${color}55,0 0 8px ${color}88} 50%{box-shadow:3px 3px 0 ${color}55,0 0 20px ${color}} }</style>` : ""}`,
        iconSize: [70, height + 30],
        iconAnchor: [8, height + 20],
      });

      const leafletMarker = L.marker([marker.lat, marker.lng], { icon })
        .addTo(mapInstanceRef.current as Parameters<typeof L.marker>[1] extends object ? object : never);

      (leafletMarker as { on: (event: string, handler: () => void) => void }).on("click", () => {
        setSelectedMarker(marker);
      });

      markersRef.current.push(leafletMarker);
    });
  }, [mapLoaded, filteredMarkers]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapRef} className="w-full h-full rounded-xl overflow-hidden" />
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-[#E8531A] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-400">3D地图加载中...</span>
          </div>
        </div>
      )}
      {/* 3D图例 */}
      {mapLoaded && (
        <div className="absolute bottom-4 left-4 bg-gray-900/80 backdrop-blur-sm rounded-lg p-3 text-xs text-gray-300">
          <div className="font-medium mb-2 text-white">柱高说明</div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-4 bg-red-500 rounded-sm"></div>
              <span>告警点（高优先级）</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-3 bg-[#E8531A] rounded-sm"></div>
              <span>工商业（按用气量）</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-sm"></div>
              <span>居民用户</span>
            </div>
          </div>
        </div>
      )}
      {selectedMarker && (
        <MarkerPopup marker={selectedMarker} onClose={() => setSelectedMarker(null)} />
      )}
    </div>
  );
}
